import type { SnippetsProvider } from '@atlassian/editor-snippets-provider/types';
import type { SnippetsPluginOptions } from '@atlassian/editor-plugin-snippets';

import {
	getSnippetsStateSnapshot,
	onCurrentFetchComplete,
	recordSnippetInsert as recordSnippetInsertInStore,
} from '../store/snippetsStore.ts';
import type { Snippet as ConfluenceSnippet } from '../types/snippet.ts';

import { mapConfluenceSnippet } from './mapConfluenceSnippet.ts';

const getConfluenceSnippets = (): Promise<ConfluenceSnippet[]> => {
	const { snippets, isLoaded, fetchError } = getSnippetsStateSnapshot();

	if (isLoaded) {
		return Promise.resolve(snippets);
	}

	if (fetchError) {
		return Promise.resolve([]);
	}

	return new Promise((resolve) => {
		onCurrentFetchComplete((loadedSnippets) => {
			resolve(loadedSnippets);
		});
	});
};

export const createConfluenceSnippetsProvider = (
	getUserId: () => string | undefined = () => undefined,
): SnippetsProvider => ({
	getSnippets: async () => {
		const snippets = await getConfluenceSnippets();
		const userId = getUserId();
		return snippets.map((snippet) => ({
			...mapConfluenceSnippet(snippet),
			...(userId && { isCreatedByCurrentUser: snippet.createdBy?.accountId === userId }),
		}));
	},
});

export const confluenceSnippetsProvider: SnippetsProvider = createConfluenceSnippetsProvider();

type CreateConfluenceSnippetsPluginOptionsParams = {
	getUserId?: () => string | undefined;
	openTemplateGallery?: () => void;
	recordSnippetInsert?: (snippetId: string) => void;
};

export const createConfluenceSnippetsPluginOptions = ({
	getUserId = () => undefined,
	openTemplateGallery,
	recordSnippetInsert = recordSnippetInsertInStore,
}: CreateConfluenceSnippetsPluginOptionsParams): SnippetsPluginOptions => ({
	...(openTemplateGallery && { openTemplateGallery }),
	snippetsProvider: createConfluenceSnippetsProvider(getUserId),
	onSnippetInserted: ({ snippet }): void => {
		recordSnippetInsert(snippet.snippetId);
	},
});
